import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Typography, Button, Alert, CircularProgress, Box, Divider, InputAdornment, IconButton
} from '@mui/material';
import { LocalFlorist, Visibility, VisibilityOff } from '@mui/icons-material';
import Input from '../components/Input';

const API_URL = process.env.REACT_APP_API_URL;
const GRADIENT = "linear-gradient(150deg, #27264A 0%, #5957a8 100%)";

export default function DefinirMotDePasse() {
    const { token } = useParams();
    const navigate = useNavigate();

    const [invitation, setInvitation] = useState(null);
    const [loadError, setLoadError] = useState('');
    const [loading, setLoading] = useState(true);

    const [prenom, setPrenom] = useState('');
    const [nom, setNom] = useState('');
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        fetch(`${API_URL}/utilisateur/invitation/${token}`)
            .then(async res => {
                const data = await res.json();
                if (!res.ok) {
                    setLoadError(data.messageFr || 'Invitation invalide');
                } else {
                    setInvitation(data);
                }
            })
            .catch(() => setLoadError('Impossible de vérifier l\'invitation'))
            .finally(() => setLoading(false));
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (password !== confirm) {
            setSubmitError('Les mots de passe ne correspondent pas');
            return;
        }
        if (password.length < 8) {
            setSubmitError('Le mot de passe doit faire au moins 8 caractères');
            return;
        }
        setIsSubmitting(true);
        setSubmitError('');

        const res = await fetch(`${API_URL}/utilisateur/invitation/definir-mot-de-passe`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token, prenom, nom, password }),
        });
        const data = await res.json();
        setIsSubmitting(false);

        if (res.ok) {
            setSuccess(true);
        } else {
            setSubmitError(data.messageFr || 'Une erreur est survenue');
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
            {/* Panel gauche */}
            <Box sx={{
                flex: { xs: '0 0 auto', md: '0 0 42%' },
                background: GRADIENT,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                p: { xs: 4, md: 6 }, color: 'white', minHeight: { xs: 200, md: '100vh' },
            }}>
                <LocalFlorist sx={{ fontSize: { xs: 48, md: 72 }, mb: 2, opacity: 0.85 }} />
                <Typography variant="h3" sx={{ fontWeight: 800, letterSpacing: 2, mb: 1, fontSize: { xs: '2rem', md: '2.8rem' } }}>
                    Sérenicim
                </Typography>
                <Divider sx={{ width: 56, borderColor: 'rgba(255,255,255,0.35)', my: 2 }} />
                <Typography variant="body1" sx={{ textAlign: 'center', opacity: 0.75, maxWidth: 280, lineHeight: 1.8 }}>
                    La gestion de cimetière<br />simple et sereine
                </Typography>
            </Box>

            {/* Panel droit */}
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#F6F6F9', p: { xs: 3, md: 6 } }}>
                <Box sx={{ width: '100%', maxWidth: 420, backgroundColor: 'white', borderRadius: 3, p: { xs: 3, md: 4 }, boxShadow: '0 4px 32px rgba(39,38,74,0.09)' }}>

                    {loading && <CircularProgress sx={{ display: 'block', mx: 'auto', my: 4 }} />}

                    {!loading && loadError && (
                        <>
                            <Typography variant="h5" sx={{ fontWeight: 700, color: '#27264A', mb: 1 }}>Lien invalide</Typography>
                            <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>
                            <Button variant="outlined" fullWidth onClick={() => navigate('/login')} sx={{ borderRadius: 2, textTransform: 'none' }}>
                                Retour à la connexion
                            </Button>
                        </>
                    )}

                    {!loading && !loadError && !success && invitation && (
                        <>
                            <Typography variant="h5" sx={{ fontWeight: 700, color: '#27264A', mb: 0.5 }}>
                                Créer votre compte
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                Vous rejoignez <strong style={{ textTransform: 'capitalize' }}>{invitation.commune}</strong> en tant que <strong>{invitation.role}</strong>.<br />
                                Compte : {invitation.email}
                            </Typography>

                            {submitError && <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert>}

                            <form onSubmit={handleSubmit}>
                                <Input name="Prénom" required onChange={setPrenom} otherProps={{ fullWidth: true }} />
                                <Input name="Nom" required onChange={setNom} style={{ marginTop: 16 }} otherProps={{ fullWidth: true }} />
                                <Input
                                    name="Mot de passe"
                                    required
                                    onChange={setPassword}
                                    style={{ marginTop: 16 }}
                                    otherProps={{ fullWidth: true, type: showPassword ? 'text' : 'password' }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => setShowPassword(p => !p)} edge="end">
                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                                <Input
                                    name="Confirmer le mot de passe"
                                    required
                                    onChange={setConfirm}
                                    style={{ marginTop: 16 }}
                                    otherProps={{ fullWidth: true, type: showPassword ? 'text' : 'password' }}
                                />
                                <Button
                                    type="submit"
                                    variant="contained"
                                    fullWidth
                                    size="large"
                                    disabled={!prenom || !nom || !password || !confirm || isSubmitting}
                                    sx={{ mt: 3, borderRadius: 2, py: 1.5, textTransform: 'none', fontSize: '1rem' }}
                                >
                                    {isSubmitting ? 'Création en cours…' : 'Créer mon compte'}
                                </Button>
                            </form>
                        </>
                    )}

                    {success && (
                        <>
                            <Alert severity="success" sx={{ mb: 3 }}>
                                Votre compte a été créé avec succès !
                            </Alert>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                Vous pouvez maintenant vous connecter avec votre adresse email et votre mot de passe.
                            </Typography>
                            <Button
                                variant="contained"
                                fullWidth
                                size="large"
                                onClick={() => navigate('/login')}
                                sx={{ borderRadius: 2, py: 1.5, textTransform: 'none', fontSize: '1rem' }}
                            >
                                Aller à la connexion
                            </Button>
                        </>
                    )}
                </Box>
            </Box>
        </Box>
    );
}
